import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

// Simple localStorage persistence plugin
const localStoragePlugin = (store) => {
  // Load state from localStorage on init
  const savedState = localStorage.getItem('iol-solver-state')
  if (savedState) {
    try {
      const parsed = JSON.parse(savedState)
      const currentTime = Date.now()
      const fiveMinutesInMs = 5 * 60 * 1000
      
      // Check if timestamp exists and is less than 5 minutes old
      if (parsed.timestamp && (currentTime - parsed.timestamp) < fiveMinutesInMs) {
        if (parsed.ginputs) store.state.ginputs = parsed.ginputs
        if (parsed.ginputs2) store.state.ginputs2 = parsed.ginputs2
        if (parsed.glastCalc) store.state.glastCalc = parsed.glastCalc
        if (parsed.currentSide !== undefined) {
          store.state.currentSide = parsed.currentSide
        }
        if (parsed.gtoric_calc_allowed !== undefined) {
          store.state.gtoric_calc_allowed = parsed.gtoric_calc_allowed
        }
      } else {
        // State is too old, clear it
        localStorage.removeItem('iol-solver-state')
      }
    } catch (e) {
      console.error('Failed to load state from localStorage:', e)
    }
  }

  // Save to localStorage on every mutation
  store.subscribe((mutation, state) => {
    try {
      localStorage.setItem(
        'iol-solver-state',
        JSON.stringify({
          timestamp: Date.now(),
          ginputs: state.ginputs,
          ginputs2: state.ginputs2,
          glastCalc: state.glastCalc,
          currentSide: state.currentSide,
          gtoric_calc_allowed: state.gtoric_calc_allowed,
        }),
      )
    } catch (e) {
      console.error('Failed to save state to localStorage:', e)
    }
  })
}

const store = new Vuex.Store({
  strict: process.env.NODE_ENV !== 'production',
  plugins: [localStoragePlugin],
  state: {
    user: null,
    ghostSettings: {
      navigation: [],
    },
    appHeader: {
      title: 'Pearl DGS Calculator®',
      html: '',
    },
    gtoric_calc_allowed: false,
    glastCalc: '', // regular(_od/_os) or complex or second
    currentSide: true, // true = OD (right eye), false = OS (left eye)
    ginputs: {
      patientID: null,
      side: null,
      keratometric_index: null,
      al: null,
      k1: null,
      k2: null,
      acd: null,
      lt: null,
      cct: null,
      biometer: null,
      iol_model: null,
      aconst: null,
      target: null,

      is_non_physio_cornea: null,
      is_icl: null,
      is_rk: null,
      is_corneal_laser: null,
      corneal_laser_type: null,
      laser_amount: null,
      pre_lvc_arc: null,
      prc_topo: null,
      tk: null,
      tcrp: null,

      is_second: null,
      al2: null,
      k12: null,
      k22: null,
      acd2: null,
      lt2: null,
      cct2: null,
      power2: null,
      se2: null,
      laser_amount2: null,
      pre_lvc_arc2: null,
    },
    results: {
      target: null,
      iolp: null,
      p1: null,
      p05: null,
      p0: null,
      pm05: null,
      pm1: null,
    },
    ginputs2: {
      patientID: null,
      side: null,
      keratometric_index: null,
      al: null,
      k1: null,
      k2: null,
      acd: null,
      lt: null,
      cct: null,
      biometer: null,
      iol_model: null,
      aconst: null,
      target: null,

      is_non_physio_cornea: null,
      is_icl: null,
      is_rk: null,
      is_corneal_laser: null,
      corneal_laser_type: null,
      laser_amount: null,
      pre_lvc_arc: null,
      prc_topo: null,
      tk: null,
      tcrp: null,

      is_second: null,
      al2: null,
      k12: null,
      k22: null,
      acd2: null,
      lt2: null,
      cct2: null,
      power2: null,
      se2: null,
      laser_amount2: null,
      pre_lvc_arc2: null,
    },
    results2: {
      target: null,
      iolp: null,
      p1: null,
      p05: null,
      p0: null,
      pm05: null,
      pm1: null,
    },
    ginputs_toric: {
      selected_power: null,
      selected_es: null,
      k1_axis: null,
      pr1: null,
      pr2: null,
      pk1: null,
      pk2: null,
      measured_post_cyl: null,
      measured_post_steep_axis: null,
      sia: null,
      sia_axis: null,
      style: null,
      notation: null,
      real_iol_axis: null,
    },
    results_toric: {
      info: null,
      p1: null,
      p05: null,
      p0: null,
      pm05: null,
      pm1: null,
    },
  },
  mutations: {
    setAuthUser(state, user) {
      state.user = user
    },
    updateToricCalcAllowed(state, new_state) {
      state.gtoric_calc_allowed = new_state
    },
    updateLastCalc(state, new_state) {
      state.glastCalc = new_state
    },
    updateCurrentSide(state, side) {
      state.currentSide = side
    },
    updateInputs(state, newInputs) {
      Object.keys(newInputs).forEach(
        (key) => !(key in state.ginputs) && delete newInputs[key],
      )
      state.ginputs = { ...state.ginputs, ...newInputs }
    },
    updateInputs2(state, newInputs2) {
      Object.keys(newInputs2).forEach(
        (key) => !(key in state.ginputs2) && delete newInputs2[key],
      )
      state.ginputs2 = { ...state.ginputs2, ...newInputs2 }
    },
    updateInputsToric(state, newInputs) {
      Object.keys(newInputs).forEach(
        (key) => !(key in state.ginputs_toric) && delete newInputs[key],
      )
      state.ginputs_toric = { ...state.ginputs_toric, ...newInputs }
    },
    resetInputs(state) {
      // Reset ginputs to null values
      Object.keys(state.ginputs).forEach((key) => {
        state.ginputs[key] = null
      })
    },
    resetInputs2(state) {
      // Reset ginputs2 to null values
      Object.keys(state.ginputs2).forEach((key) => {
        state.ginputs2[key] = null
      })
    },
    resetInputsToric(state) {
      // Reset ginputs_toric to null values
      Object.keys(state.ginputs_toric).forEach((key) => {
        state.ginputs_toric[key] = null
      })
    },
    setGhostSettings(state, ghostSettings) {
      // Remove link with https aka "internal app link" ()
      ghostSettings.navigation = ghostSettings.navigation.filter(
        (item) => !item.url.startsWith('https'),
      )
      state.ghostSettings = ghostSettings
    },
    setResults(state, newResults) {
      state.results = newResults
    },
    setResults2(state, newResults2) {
      state.results2 = newResults2
    },
    setResultsToric(state, newResultsToric) {
      state.results_toric = newResultsToric
    },
    setAppHeader(state, page) {
      // page is content of app-header page from ghost API
      state.appHeader = page
    },
  },
  getters: {
    isLoggedIn(state) {
      return localStorage.getItem('jwt-token') !== null
    },
  },
  actions: {
    updateToricCalcAllowed({ commit }, new_state) {
      commit('updateToricCalcAllowed', new_state)
    },
    updateLastCalc({ commit }, new_state) {
      commit('updateLastCalc', new_state)
    },
    updateCurrentSide({ commit }, side) {
      commit('updateCurrentSide', side)
    },
    updateInputs({ commit }, newInputs) {
      commit('updateInputs', newInputs)
    },
    updateInputs2({ commit }, newInputs2) {
      commit('updateInputs2', newInputs2)
    },
    updateInputsToric({ commit }, newInputs) {
      commit('updateInputsToric', newInputs)
    },
    resetInputs({ commit }) {
      commit('resetInputs')
    },
    resetInputs2({ commit }) {
      commit('resetInputs2')
    },
    resetInputsToric({ commit }) {
      commit('resetInputsToric')
    },
    setResults({ commit }, newResults) {
      commit('setResults', newResults)
    },
    setResults2({ commit }, newResults2) {
      commit('setResults2', newResults2)
    },
    setResultsToric({ commit }, newResults) {
      commit('setResultsToric', newResults)
    },
    setAuthUser({ commit }, user) {
      commit('setAuthUser', user)
    },
    setGhostSettings({ commit }, ghostSettings) {
      commit('setGhostSettings', ghostSettings)
    },
    setAppHeader({ commit }, page) {
      commit('setAppHeader', page)
    },
  },
})

export default store
